import { Alerts } from "../../../utils/Alerts.js";
import { DateTime as DateTime$1 } from "../../../components/DateTime/index.js";
import { getTHERMSImageThumbnailUrl } from "../../Files/utils/get-therms-image-thumbnail-url.js";
import { useRPCContext } from "../../../contexts/RPCContext.js";
import { UserName } from "../../Users/shared/UserName.js";
import { useLocation } from "../../Locations/hooks/useLocation.js";
import { UserAnPCheck } from "../../shared/UserAnPCheck.js";
import { ErrorLoadingRetry } from "../../../components/ErrorLoadingRetry.js";
import { useRPCServerEventHandler } from "../../../hooks/useRPCServerEventHandler/index.js";
import { useSchedulingAssignment } from "../hooks/useSchedulingAssignment.js";
import { useSchedulingUser } from "../hooks/useSchedulingUser.js";
import { useSchedulingUsers } from "../hooks/useSchedulingUsers.js";
import { useShiftDetail } from "../hooks/useShiftDetail.js";
import { ShiftProgressBar } from "./ShiftProgressBar/index.js";
import { SelectSchedulingUser } from "./SelectSchedulingUser.js";
import { ShiftActionsButtonGroup } from "./ShiftActionsButtonGroup.js";
import { ShiftGpsReviewModal } from "./ShiftGpsReviewModal.js";
import { useUserPositionOption } from "../../Users/hooks/useUserPositionOption.js";
import { ShiftStatusLabel } from "./ShiftStatusLabel.js";
import { ShiftTimeLogStatusLabel } from "./ShiftTimeLogStatusLabel.js";
import { ShiftFlagList } from "./shift-flags/ShiftFlagList.js";
import { ShiftGpsWindows } from "./shift-flags/ShiftGpsWindows.js";
import { UserPosition } from "../../Users/shared/UserPosition.js";
import dayjs from "dayjs";
import { useState } from "react";
import { Avatar, Button, ColorSquare, Icon, Label, Loading, Modal, Timeline } from "@therms/atalaya";
import { IoClose } from "react-icons/io5";
import { FaMapMarkedAlt } from "react-icons/fa";
import { jsx, jsxs } from "react/jsx-runtime";
import { RPCDocs } from "@therms/rpc-gateway";
import { formatAddress } from "@therms/web-js";
import classnames from "classnames";
import { MdClose } from "react-icons/md";
import { PiUserSwitchFill } from "react-icons/pi";
//#region src/modules/Scheduling/shared/ShiftInteractiveModal.tsx
var Action = RPCDocs.Scheduling.GetShiftDetail.v1.Action;
var RoleElement = RPCDocs.Auth.GetIdentityInfo.v1.Roles;
function TimelineItemRender({ action, userId }) {
	let actionLabel = "";
	if (action === Action.Startbreak) actionLabel = "Break Started";
	else if (action === Action.Endbreak) actionLabel = "Break Ended";
	else if (action === Action.Startshift) actionLabel = "Shift Started";
	else if (action === Action.Endshift) actionLabel = "Shift Ended";
	return /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("div", {
		className: "font-medium",
		children: actionLabel
	}), /* @__PURE__ */ jsx("div", {
		className: "text-color-subtle text-sm",
		children: /* @__PURE__ */ jsx(UserName, { userId })
	})] });
}
function ShiftInteractiveModal({ closeHandler, interactive = true, modalHeaderRight, shiftId }) {
	const [isChangingAssignedUser, setIsChangingActingUser] = useState(false);
	const [isLoadingUserChange, setIsLoadingUserChange] = useState(false);
	const [mapOpen, setMapOpen] = useState(false);
	const { data: shift, loading: shiftLoading, refresh } = useShiftDetail(shiftId);
	const { rpcService } = useRPCContext();
	useRPCServerEventHandler("CurrentShiftsUpdated", (event, { shiftIds }) => {
		if (shiftIds.includes(shiftId)) refresh();
	});
	const { data: assignment, loading: assignmentLoading } = useSchedulingAssignment(shift?.assignmentId, { cacheStrategy: "cache-only" });
	const { data: position, loading: positionLoading } = useUserPositionOption(shift?.positionId, { cacheStrategy: "cache-only" });
	const { data: schedulingUser, loading: userLoading } = useSchedulingUser(shift?.userId, { cacheStrategy: "cache-only" });
	const { data: schedulingUsersData } = useSchedulingUsers({
		active: true,
		pagination: { limit: 2e3 }
	}, { cacheStrategy: "cache-only" });
	const { data: locationData } = useLocation(assignment?.metadata.locationId ? { locationId: assignment.metadata.locationId } : void 0, { cacheStrategy: "cache-only" });
	const handleAssignedUserChange = async (newUserId) => {
		const scheduledShiftId = shift?.scheduledShiftId;
		if (scheduledShiftId) {
			setIsLoadingUserChange(true);
			await rpcService.Scheduling.UpdateScheduledShift.v1.call({ scheduledShift: {
				id: scheduledShiftId,
				userId: newUserId
			} }).then(() => {
				rpcService.Scheduling.PublishScheduledShifts.v1.call({ scheduledShiftIds: [scheduledShiftId] });
			}).then(() => {
				refresh();
			}).catch((error) => {
				console.log(error);
				Alerts.error("There was an error changing the assigned user", error.message);
			}).finally(() => {
				setIsLoadingUserChange(false);
			});
		}
	};
	const loading = assignmentLoading || positionLoading || userLoading || shiftLoading;
	const assignmentGeo = assignment?.config?.geo;
	const locationGeo = locationData?.location?.geo;
	const perimeter = assignmentGeo?.perimeter ?? locationGeo?.perimeter;
	const driftMeters = assignmentGeo?.perimeter ? void 0 : locationGeo?.gpsDriftMeters;
	if (!shift) return /* @__PURE__ */ jsx(Modal, {
		closeHandler,
		outsideClickClose: true,
		showClose: false,
		width: "480px",
		children: loading ? /* @__PURE__ */ jsx("div", {
			className: "h-52",
			children: /* @__PURE__ */ jsx(Loading, {
				overlay: true,
				size: "xl"
			})
		}) : /* @__PURE__ */ jsx(ErrorLoadingRetry, { onRetry: refresh })
	});
	const { color, durationHr, endTime, notes, positionId, qualificationIds, startTime, timeLog, title } = shift;
	return /* @__PURE__ */ jsxs(Modal, {
		closeHandler,
		outsideClickClose: true,
		showClose: false,
		children: [
			loading && /* @__PURE__ */ jsx(Loading, {
				overlay: true,
				size: "xl"
			}),
			!!assignment?.primaryImageUrl && /* @__PURE__ */ jsxs("div", {
				className: "absolute inset-x-0 top-0 h-36 overflow-hidden",
				children: [/* @__PURE__ */ jsx("img", {
					className: "h-36 w-full rounded-t object-cover opacity-70 blur-[2px]",
					src: assignment.primaryImageUrl,
					alt: ""
				}), /* @__PURE__ */ jsx("div", { className: "from-surface absolute inset-0 rounded-t bg-linear-to-t" })]
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "relative",
				children: [
					/* @__PURE__ */ jsxs("div", {
						className: "flex items-end justify-between",
						children: [/* @__PURE__ */ jsx("span", {
							className: "mb-xxs text-sm font-medium tracking-wide",
							children: dayjs(startTime).format("dddd, MMM Do")
						}), /* @__PURE__ */ jsxs("div", {
							className: "gap-xs bg-surface flex items-center rounded",
							children: [modalHeaderRight, /* @__PURE__ */ jsx(Button, {
								onClick: closeHandler,
								subtle: true,
								children: /* @__PURE__ */ jsx(Icon, { Icon: IoClose })
							})]
						})]
					}),
					assignment?.title ? /* @__PURE__ */ jsx("div", {
						className: "text-color-strong text-xl font-bold tracking-wide",
						children: assignment.title
					}) : /* @__PURE__ */ jsx("div", {
						className: "text-color-subtle text-xl font-bold tracking-wide",
						children: "No Assignment"
					}),
					!!assignment?.subTitle && /* @__PURE__ */ jsx("div", {
						className: "tracking-wider",
						children: assignment.subTitle
					}),
					/* @__PURE__ */ jsxs("div", {
						className: "mt-base flex h-8 font-bold",
						children: [/* @__PURE__ */ jsxs(ShiftProgressBar, {
							startTime,
							endTime,
							color: shift.status?.color || color,
							className: classnames({
								rounded: !durationHr,
								"rounded-l": durationHr
							}, "text-color-strong relative grow shadow-inner"),
							children: [/* @__PURE__ */ jsx("div", {
								className: "left-sm absolute top-1/2 -translate-y-1/2",
								children: /* @__PURE__ */ jsx(DateTime$1, {
									timestamp: startTime,
									mode: "time",
									toggleRelativeOnClick: false
								})
							}), /* @__PURE__ */ jsx("div", {
								className: "right-sm absolute top-1/2 -translate-y-1/2",
								children: endTime ? /* @__PURE__ */ jsx(DateTime$1, {
									timestamp: endTime,
									mode: "time",
									toggleRelativeOnClick: false
								}) : "--:--"
							})]
						}), durationHr && /* @__PURE__ */ jsxs("div", {
							className: "bg-surface-strong px-sm flex items-center rounded-r",
							children: [durationHr, "h"]
						})]
					}),
					/* @__PURE__ */ jsxs("div", {
						className: "my-sm grid grid-cols-2",
						children: [/* @__PURE__ */ jsxs("div", {
							className: "space-x-xs flex",
							children: [/* @__PURE__ */ jsx(Label, { children: "Status" }), /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(ShiftStatusLabel, { shift }) })]
						}), /* @__PURE__ */ jsxs("div", {
							className: "space-x-xs flex",
							children: [/* @__PURE__ */ jsx(Label, { children: "Last Action" }), /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(ShiftTimeLogStatusLabel, { shift }) })]
						})]
					}),
					Array.isArray(shift.flags) && shift.flags.length > 0 && /* @__PURE__ */ jsxs("div", {
						className: "mt-sm",
						children: [/* @__PURE__ */ jsx(Label, { children: "Flags" }), /* @__PURE__ */ jsxs("div", {
							className: "mt-xxs",
							children: [
								/* @__PURE__ */ jsx(ShiftFlagList, { flags: shift.flags }),
								/* @__PURE__ */ jsx(ShiftGpsWindows, { flags: shift.flags }),
								!!shift.userId && !!startTime && /* @__PURE__ */ jsx("div", {
									className: "mt-xs",
									children: /* @__PURE__ */ jsxs(Button, {
										onClick: () => setMapOpen(true),
										size: "sm",
										subtle: true,
										children: [/* @__PURE__ */ jsx(Icon, {
											className: "mr-xxs",
											Icon: FaMapMarkedAlt,
											size: "sm"
										}), "View on map"]
									})
								})
							]
						})]
					}),
					!!shift.userId && !!startTime && /* @__PURE__ */ jsx(ShiftGpsReviewModal, {
						driftMeters,
						fromDate: startTime,
						isOpen: mapOpen,
						onClose: () => setMapOpen(false),
						perimeter,
						toDate: endTime ?? (/* @__PURE__ */ new Date()).toISOString(),
						userId: shift.userId
					}),
					/* @__PURE__ */ jsx("div", {
						className: "mt-base",
						children: interactive && /* @__PURE__ */ jsx(ShiftActionsButtonGroup, { shift })
					}),
					/* @__PURE__ */ jsx("div", { className: "mt-xl" }),
					!!title && /* @__PURE__ */ jsxs("div", {
						className: "mt-base",
						children: [/* @__PURE__ */ jsx(Label, { children: "Shift Name" }), /* @__PURE__ */ jsx("div", {
							className: "space-x-sm flex items-center",
							children: title
						})]
					}),
					!isChangingAssignedUser ? /* @__PURE__ */ jsxs("div", {
						className: "mt-sm",
						children: [/* @__PURE__ */ jsx(Label, { children: "User Assigned" }), /* @__PURE__ */ jsxs("div", {
							className: "mt-xxs flex justify-between",
							children: [/* @__PURE__ */ jsxs("div", {
								className: "gap-x-sm flex items-center",
								children: [!!schedulingUser?.user.avatarUrl && /* @__PURE__ */ jsx(Avatar, {
									circular: true,
									size: "sm",
									url: getTHERMSImageThumbnailUrl(schedulingUser.user.avatarUrl)
								}), /* @__PURE__ */ jsx("span", { children: schedulingUser?.user.displayName })]
							}), /* @__PURE__ */ jsx(UserAnPCheck, {
								check: (anp) => anp.anp.roles.includes(RoleElement.Scheduling),
								children: /* @__PURE__ */ jsxs(Button, {
									onClick: () => setIsChangingActingUser(true),
									size: "sm",
									subtle: true,
									children: [/* @__PURE__ */ jsx(Icon, {
										className: "mr-xxs",
										Icon: PiUserSwitchFill,
										size: "sm"
									}), "Change"]
								})
							})]
						})]
					}) : /* @__PURE__ */ jsxs("div", {
						className: "mt-sm relative",
						children: [isLoadingUserChange && /* @__PURE__ */ jsx(Loading, { overlay: true }), !!schedulingUsersData && /* @__PURE__ */ jsxs("div", {
							className: "gap-xs flex items-end",
							children: [/* @__PURE__ */ jsx("div", {
								className: "grow",
								children: /* @__PURE__ */ jsx(SelectSchedulingUser, {
									isClearable: false,
									label: /* @__PURE__ */ jsx("span", {
										className: "flex items-center",
										children: /* @__PURE__ */ jsx("span", { children: "Select A New User to Assign" })
									}),
									limit: 2e3,
									name: "assignedUserId",
									onChangeValue: async (newUserId) => {
										await handleAssignedUserChange(newUserId);
										setIsChangingActingUser(false);
									},
									value: shift.userId
								})
							}), /* @__PURE__ */ jsx(Button, {
								onClick: () => setIsChangingActingUser(false),
								subtle: true,
								children: /* @__PURE__ */ jsx(Icon, { Icon: MdClose })
							})]
						})]
					}),
					!!locationData?.location && /* @__PURE__ */ jsxs("div", {
						className: "mt-sm",
						children: [/* @__PURE__ */ jsx(Label, { children: "Location" }), /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("span", { children: locationData.location.name }), !!locationData.location.address && /* @__PURE__ */ jsxs("span", {
							className: "text-color-subtle",
							children: [" ", formatAddress(locationData.location.address)]
						})] })]
					}),
					!!positionId && /* @__PURE__ */ jsxs("div", {
						className: "mt-sm",
						children: [/* @__PURE__ */ jsx(Label, { children: "Position" }), /* @__PURE__ */ jsxs("div", {
							className: "space-x-xs flex items-center",
							children: [!!position?.color && /* @__PURE__ */ jsx(ColorSquare, { color: position.color }), /* @__PURE__ */ jsx(UserPosition, { id: positionId })]
						})]
					}),
					Array.isArray(qualificationIds) && qualificationIds.length && /* @__PURE__ */ jsxs("div", {
						className: "mt-sm",
						children: [/* @__PURE__ */ jsx(Label, { children: "Qualifications" }), /* @__PURE__ */ jsx("div", { children: qualificationIds.map((qualificationId) => `${qualificationId}, `) })]
					}),
					!!notes && /* @__PURE__ */ jsxs("div", {
						className: "mt-sm",
						children: [/* @__PURE__ */ jsx(Label, { children: "Notes" }), /* @__PURE__ */ jsx("div", { children: notes })]
					}),
					/* @__PURE__ */ jsx("div", { children: Array.isArray(timeLog) && !!timeLog.length && /* @__PURE__ */ jsxs("div", {
						className: "mt-sm",
						children: [/* @__PURE__ */ jsx(Label, { children: "Time Log" }), /* @__PURE__ */ jsx("div", {
							className: "mt-sm",
							children: /* @__PURE__ */ jsx(Timeline, {
								data: timeLog,
								render: TimelineItemRender,
								renderTimestamp: ({ timestamp }) => /* @__PURE__ */ jsx(DateTime$1, {
									timestamp,
									relative: false,
									toggleRelativeOnClick: false
								})
							})
						})]
					}) })
				]
			})
		]
	});
}
//#endregion
export { ShiftInteractiveModal };

//# sourceMappingURL=ShiftInteractiveModal.js.map